import {register, CloudObject, Tag, Transaction, defineProperty, Predicate, Query} from 'olympe';

/**
 * @extends {CloudObject}
 */
export default class FavoriteFilters extends CloudObject {
    /**
     * Creates an instance of FavoriteFilters.
     *
     * @param {!Transaction} transaction
     * @return {!string} the tag of the created instance
     */
    static create(transaction) {
        return transaction.create(FavoriteFilters);
    }

    /**
     * Create an instance of FavoriteFilters, based on the properties of the filters given as a parameter
     * @param {Transaction} transaction
     * @param {Filters} filters
     * @param {string} userId
     * @param {string} name
     * @return {FavoriteFilters}
     */
    static createFromFilterObject(transaction, filters, userId, name) {
        const properties = new Map([
            [FavoriteFilters.nameProp, name],
            [FavoriteFilters.creationDateProp, new Date()],
            [FavoriteFilters.userIdProp, userId.toUpperCase()],
            [FavoriteFilters.allSelectedProp, filters.allSelected],
            [FavoriteFilters.startCreationDateProp, JSON.stringify(filters.startCreationDate)],
            [FavoriteFilters.endCreationDateProp, JSON.stringify(filters.endCreationDate)],
            [FavoriteFilters.startRequestedDeliveryDateProp, JSON.stringify(filters.startRequestedDeliveryDate)],
            [FavoriteFilters.endRequestedDeliveryDateProp, JSON.stringify(filters.endRequestedDeliveryDate)],
            [FavoriteFilters.categoriesProp, JSON.stringify(filters.categories)],
            [FavoriteFilters.companyCodesProp, JSON.stringify(filters.companyCode)],
            [FavoriteFilters.defaultPlantsProp, JSON.stringify(filters.defaultPlant)],
            [FavoriteFilters.defaultStorageLocProp, JSON.stringify(filters.defaultStorageLocation)],
            [FavoriteFilters.idsProp, JSON.stringify(filters.ids)],
            [FavoriteFilters.materialsProp, JSON.stringify(filters.materials)],
            [FavoriteFilters.paymentTermsProp, JSON.stringify(filters.paymentTerms)],
            [FavoriteFilters.prNumbersProp, JSON.stringify(filters.prNumber)],
            [FavoriteFilters.projectTitlesProp, JSON.stringify(filters.projectTitle)],
            [FavoriteFilters.purchaseOrgsProp, JSON.stringify(filters.purchaseOrganization)],
            [FavoriteFilters.purchasingGrpProp, JSON.stringify(filters.purchasingGroup)],
            [FavoriteFilters.requestersProp, JSON.stringify(filters.requesters)],
            [FavoriteFilters.sourcesProp, JSON.stringify(filters.sources)],
            [FavoriteFilters.spendersProp, JSON.stringify(filters.spenders)],
            [FavoriteFilters.statusesProp, JSON.stringify(filters.statuses)],
            [FavoriteFilters.supplierStatusesProp, JSON.stringify(filters.supplierStatus)],
            [FavoriteFilters.suppliersProp, JSON.stringify(filters.suppliers)],
            [FavoriteFilters.waysOfBuyingProp, JSON.stringify(filters.wayOfBuying)],
        ]);

        return transaction.create(FavoriteFilters, properties);
    }

    /**
     * @param {BrickContext} $
     * @param {string} userId
     * @return {Promise<Array<{tag: string, metadata: {name: string, creationDate: Date}, filters: Filters[]}>>}
     */
    static getMyFavoriteFilters($, userId) {
        return Query
            .instancesOf(this)
            .filter(Predicate.contains(FavoriteFilters.userIdProp, userId.toUpperCase(), false))
            .limit(100, 0)
            .execute()
            .then((result) => {
                return result.toArray().map((favFilter) => ({
                    tag: favFilter.getTag(),
                    metadata: {
                        name: favFilter.getName(),
                        creationDate: favFilter.getCreationDate(),
                    },
                    filters: {
                        allSelected: favFilter.getAllSelected(),
                        startCreationDate: JSON.parse(favFilter.getStartCreationDate()),
                        endCreationDate: JSON.parse(favFilter.getEndCreationDate()),
                        startRequestedDeliveryDate: JSON.parse(favFilter.getStartRequestedDeliveryDate()),
                        endRequestedDeliveryDate: JSON.parse(favFilter.getEndRequestedDeliveryDate()),
                        categories: JSON.parse(favFilter.getCategories()) ?? [],
                        companyCode: JSON.parse(favFilter.getCompanyCodes()) ?? [],
                        defaultPlant: JSON.parse(favFilter.getDefaultPlants()) ?? [],
                        defaultStorageLocation: JSON.parse(favFilter.getDefaultStorageLoc()) ?? [],
                        ids: JSON.parse(favFilter.getIds()) ?? [],
                        materials: JSON.parse(favFilter.getMaterials()) ?? [],
                        paymentTerms: JSON.parse(favFilter.getPaymentTerms()) ?? [],
                        prNumber: JSON.parse(favFilter.getPrNumbers()) ?? [],
                        projectTitle: JSON.parse(favFilter.getProjectTitles()) ?? [],
                        purchaseOrganization: JSON.parse(favFilter.getPurchaseOrgs()) ?? [],
                        purchasingGroup: JSON.parse(favFilter.getPurchasingGrp()) ?? [],
                        requesters: JSON.parse(favFilter.getRequesters()) ?? [],
                        sources: JSON.parse(favFilter.getSources()) ?? [],
                        spenders: JSON.parse(favFilter.getSpenders()) ?? [],
                        statuses: JSON.parse(favFilter.getStatuses()) ?? [],
                        supplierStatus: JSON.parse(favFilter.getSupplierStatuses()) ?? [],
                        suppliers: JSON.parse(favFilter.getSuppliers()) ?? [],
                        wayOfBuying: JSON.parse(favFilter.getWaysOfBuying()) ?? [],
                    },
                }));
            });
    }

    /**
     * @return {string}
     */
    getName() {
        return this.get(FavoriteFilters.nameProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeName($) {
        return this.observe($, FavoriteFilters.nameProp);
    }

    /**
     * @return {Date}
     */
    getCreationDate() {
        return this.get(FavoriteFilters.creationDateProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<Date>}
     */
    observeCreationDate($) {
        return this.observe($, FavoriteFilters.creationDateProp);
    }

    /**
     * @return {boolean}
     */
    getAllSelected() {
        return this.get(FavoriteFilters.allSelectedProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<boolean>}
     */
    observeAllSelected($) {
        return this.observe($, FavoriteFilters.allSelectedProp);
    }

    /**
     * @return {string}
     */
    getStartCreationDate() {
        return this.get(FavoriteFilters.startCreationDateProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeStartCreationDate($) {
        return this.observe($, FavoriteFilters.startCreationDateProp);
    }

    /**
     * @return {string}
     */
    getEndCreationDate() {
        return this.get(FavoriteFilters.endCreationDateProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeEndCreationDate($) {
        return this.observe($, FavoriteFilters.endCreationDateProp);
    }

    /**
     * @return {string}
     */
    getStartRequestedDeliveryDate() {
        return this.get(FavoriteFilters.startRequestedDeliveryDateProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeStartRequestedDeliveryDate($) {
        return this.observe($, FavoriteFilters.startRequestedDeliveryDateProp);
    }

    /**
     * @return {string}
     */
    getEndRequestedDeliveryDate() {
        return this.get(FavoriteFilters.endRequestedDeliveryDateProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeEndRequestedDeliveryDate($) {
        return this.observe($, FavoriteFilters.endRequestedDeliveryDateProp);
    }

    /**
     * @return {string}
     */
    getCategories() {
        return this.get(FavoriteFilters.categoriesProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeCategories($) {
        return this.observe($, FavoriteFilters.categoriesProp);
    }

    /**
     * @return {string}
     */
    getCompanyCodes() {
        return this.get(FavoriteFilters.companyCodesProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeCompanyCodes($) {
        return this.observe($, FavoriteFilters.companyCodesProp);
    }

    /**
     * @return {string}
     */
    getDefaultPlants() {
        return this.get(FavoriteFilters.defaultPlantsProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeDefaultPlants($) {
        return this.observe($, FavoriteFilters.defaultPlantsProp);
    }

    /**
     * @return {string}
     */
    getDefaultStorageLoc() {
        return this.get(FavoriteFilters.defaultStorageLocProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeDefaultStorageLoc($) {
        return this.observe($, FavoriteFilters.defaultStorageLocProp);
    }

    /**
     * @return {string}
     */
    getIds() {
        return this.get(FavoriteFilters.idsProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeIds($) {
        return this.observe($, FavoriteFilters.idsProp);
    }

    /**
     * @return {string}
     */
    getMaterials() {
        return this.get(FavoriteFilters.materialsProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeMaterials($) {
        return this.observe($, FavoriteFilters.materialsProp);
    }

    /**
     * @return {string}
     */
    getPaymentTerms() {
        return this.get(FavoriteFilters.paymentTermsProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observePaymentTerms($) {
        return this.observe($, FavoriteFilters.paymentTermsProp);
    }

    /**
     * @return {string}
     */
    getPrNumbers() {
        return this.get(FavoriteFilters.prNumbersProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observePrNumbers($) {
        return this.observe($, FavoriteFilters.prNumbersProp);
    }

    /**
     * @return {string}
     */
    getProjectTitles() {
        return this.get(FavoriteFilters.projectTitlesProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeProjectTitles($) {
        return this.observe($, FavoriteFilters.projectTitlesProp);
    }

    /**
     * @return {string}
     */
    getPurchaseOrgs() {
        return this.get(FavoriteFilters.purchaseOrgsProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observePurchaseOrgs($) {
        return this.observe($, FavoriteFilters.purchaseOrgsProp);
    }

    /**
     * @return {string}
     */
    getPurchasingGrp() {
        return this.get(FavoriteFilters.purchasingGrpProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observePurchasingGrp($) {
        return this.observe($, FavoriteFilters.purchasingGrpProp);
    }

    /**
     * @return {string}
     */
    getRequesters() {
        return this.get(FavoriteFilters.requestersProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeRequesters($) {
        return this.observe($, FavoriteFilters.requestersProp);
    }

    /**
     * @return {string}
     */
    getSources() {
        return this.get(FavoriteFilters.sourcesProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeSources($) {
        return this.observe($, FavoriteFilters.sourcesProp);
    }

    /**
     * @return {string}
     */
    getSpenders() {
        return this.get(FavoriteFilters.spendersProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeSpenders($) {
        return this.observe($, FavoriteFilters.spendersProp);
    }

    /**
     * @return {string}
     */
    getStatuses() {
        return this.get(FavoriteFilters.statusesProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeStatuses($) {
        return this.observe($, FavoriteFilters.statusesProp);
    }

    /**
     * @return {string}
     */
    getSupplierStatuses() {
        return this.get(FavoriteFilters.supplierStatusesProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeSupplierStatuses($) {
        return this.observe($, FavoriteFilters.supplierStatusesProp);
    }

    /**
     * @return {string}
     */
    getSuppliers() {
        return this.get(FavoriteFilters.suppliersProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeSuppliers($) {
        return this.observe($, FavoriteFilters.suppliersProp);
    }

    /**
     * @return {string}
     */
    getWaysOfBuying() {
        return this.get(FavoriteFilters.waysOfBuyingProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeWaysOfBuying($) {
        return this.observe($, FavoriteFilters.waysOfBuyingProp);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setName(transaction, instance, value) {
        transaction.update(instance, FavoriteFilters.nameProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {Date} value
     */
    static setCreationDate(transaction, instance, value) {
        transaction.update(instance, FavoriteFilters.creationDateProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {boolean} value
     */
    static setAllSelected(transaction, instance, value) {
        transaction.update(instance, FavoriteFilters.allSelectedProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setStartCreationDate(transaction, instance, value) {
        transaction.update(instance, FavoriteFilters.startCreationDateProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setEndCreationDate(transaction, instance, value) {
        transaction.update(instance, FavoriteFilters.endCreationDateProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setStartRequestedDeliveryDate(transaction, instance, value) {
        transaction.update(instance, FavoriteFilters.startRequestedDeliveryDateProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setEndRequestedDeliveryDate(transaction, instance, value) {
        transaction.update(instance, FavoriteFilters.endRequestedDeliveryDateProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setCategories(transaction, instance, value) {
        transaction.update(instance, FavoriteFilters.categoriesProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setCompanyCodes(transaction, instance, value) {
        transaction.update(instance, FavoriteFilters.companyCodesProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setDefaultPlants(transaction, instance, value) {
        transaction.update(instance, FavoriteFilters.defaultPlantsProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setDefaultStorageLoc(transaction, instance, value) {
        transaction.update(instance, FavoriteFilters.defaultStorageLocProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setIds(transaction, instance, value) {
        transaction.update(instance, FavoriteFilters.idsProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setMaterials(transaction, instance, value) {
        transaction.update(instance, FavoriteFilters.materialsProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setPaymentTerms(transaction, instance, value) {
        transaction.update(instance, FavoriteFilters.paymentTermsProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setPrNumbers(transaction, instance, value) {
        transaction.update(instance, FavoriteFilters.prNumbersProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setProjectTitles(transaction, instance, value) {
        transaction.update(instance, FavoriteFilters.projectTitlesProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setPurchaseOrgs(transaction, instance, value) {
        transaction.update(instance, FavoriteFilters.purchaseOrgsProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setRequesters(transaction, instance, value) {
        transaction.update(instance, FavoriteFilters.requestersProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setSources(transaction, instance, value) {
        transaction.update(instance, FavoriteFilters.sourcesProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setSpenders(transaction, instance, value) {
        transaction.update(instance, FavoriteFilters.spendersProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setStatuses(transaction, instance, value) {
        transaction.update(instance, FavoriteFilters.statusesProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setSupplierStatuses(transaction, instance, value) {
        transaction.update(instance, FavoriteFilters.supplierStatusesProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setSuppliers(transaction, instance, value) {
        transaction.update(instance, FavoriteFilters.suppliersProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setWaysOfBuying(transaction, instance, value) {
        transaction.update(instance, FavoriteFilters.waysOfBuyingProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setPurchasingGrp(transaction, instance, value) {
        transaction.update(instance, FavoriteFilters.purchasingGrpProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setUserId(transaction, instance, value) {
        transaction.update(instance, FavoriteFilters.userIdProp, value);
    }
}

// Hardcoded tags
register('018ab7b09dca51fccca6', FavoriteFilters);
FavoriteFilters.nameProp = defineProperty('018ab7b283dcea43f5c0');
FavoriteFilters.creationDateProp = defineProperty('018ab7b2a1c74504b1c5');
FavoriteFilters.allSelectedProp = defineProperty('018ab7b411f21c5ae9f1');
FavoriteFilters.startCreationDateProp = defineProperty('018ab7bb45a6aa3530c2');
FavoriteFilters.endCreationDateProp = defineProperty('018ab7bb64cd441bef63');
FavoriteFilters.startRequestedDeliveryDateProp = defineProperty('018ab7bb79d08fcfc692');
FavoriteFilters.endRequestedDeliveryDateProp = defineProperty('018ab7bb92dbe59fddd3');
FavoriteFilters.categoriesProp = defineProperty('018ab82dcd5864545625');
FavoriteFilters.companyCodesProp = defineProperty('018ab82e02a052dbd031');
FavoriteFilters.defaultPlantsProp = defineProperty('018ab82e2e4fd06744d1');
FavoriteFilters.defaultStorageLocProp = defineProperty('018ab82e537ffbdb7fb1');
FavoriteFilters.idsProp = defineProperty('018ab82e74a038b2e9fa');
FavoriteFilters.materialsProp = defineProperty('018ab82e8cef84d63e9a');
FavoriteFilters.paymentTermsProp = defineProperty('018ab82ea9cbcb3d4fff');
FavoriteFilters.prNumbersProp = defineProperty('018ab82ecafa18795915');
FavoriteFilters.projectTitlesProp = defineProperty('018ab82eeb6846d4392f');
FavoriteFilters.purchaseOrgsProp = defineProperty('018ab82f0c80b429cc0b');
FavoriteFilters.purchasingGrpProp = defineProperty('018abc5b6a270bd8566f');
FavoriteFilters.requestersProp = defineProperty('018ab82f2d062b2e4a7f');
FavoriteFilters.sourcesProp = defineProperty('018ab82f4d807c38c872');
FavoriteFilters.spendersProp = defineProperty('018ab82f69711f4b115a');
FavoriteFilters.statusesProp = defineProperty('018ab82f84e891e05146');
FavoriteFilters.supplierStatusesProp = defineProperty('018ab82fa06287983342');
FavoriteFilters.suppliersProp = defineProperty('018ab82fbe739ced20ad');
FavoriteFilters.waysOfBuyingProp = defineProperty('018ab82fdba5901ef94b');
FavoriteFilters.userIdProp = defineProperty('018abbdf3d3a2fc0cc0a');
